import {
	PdfTemplateConfiguration,
	RTF,
	usePdfTemplateConfigurationContext,
} from "../../context/PdfTemplateConfigurationContext";
import { extractCurrencyConverterPairs } from "../../pdf-templates/utils";
import { KostenvoranschlagTemplate } from "../../pdf-templates/pdf-templates/kostenvoranschlag.template";
import { LieferscheinTemplate } from "../../pdf-templates/pdf-templates/lieferschein.template";
import { GutschriftTemplate } from "../../pdf-templates/pdf-templates/gutschrift.template";
import { AuftragsblattTemplate } from "../../pdf-templates/pdf-templates/auftragsblatt.template";
import { RechnungTemplate } from "../../pdf-templates/pdf-templates/rechnung.template";
import { SammelrechnungTemplate } from "../../pdf-templates/pdf-templates/sammelrechnung.template";
import { BarzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/barzahlungsquittung.template";
import { KartenzahlungsquittungTemplate } from "../../pdf-templates/pdf-templates/kartenzahlungsquittung.template";
import { AkontorechnungTemplate } from "../../pdf-templates/pdf-templates/akontorechnung.template";
import { JobOrderLabelTemplate } from "../../pdf-templates/pdf-templates/job-order-label.template";
import { DebitorenExportTemplate } from "../../pdf-templates/pdf-templates/debitoren-export.template";
import {
	PAYMENT_REMINDER_PAGE_BREAK,
	PaymentReminderTemplate,
} from "../../pdf-templates/pdf-templates/payment-reminder.template";
import { PaymentReminderDocument } from "../../pages/debitoren/mahnwesen/payment-reminder.types";
import {
	getReminderSlipError,
	hasReminderSlips,
} from "../../pages/debitoren/mahnwesen/utils/payment-reminder-document";
import { useExportPdf } from "../useExportPdf";
import { trpc } from "@/lib/trpc";
import {
	ExportRowData,
	ExportSummary,
	ExportHeaderMetadata,
} from "../../lib/utils/export-utils";
import { createDebitorenHeaderTemplate } from "../../pdf-templates/pdf-template-components/pdf-debitoren-header-template.component";
import {
	ClientEntityType,
	GuarantorEntityType,
	JobDocumentEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
	PatientEntityType,
	PractitionerEntityType,
} from "@/lib/supabase/supabaseTypes";
import {
	CalculateTotalsResult,
	ExtendedJobDocument,
	calculateJobTotalsLegacy,
	calculateSammelrechnungRowsAndTotals,
	getMwStPercentageForSammelrechnung,
} from "../../lib/utils/calculate";
import { PaymentSlipDataInput } from "../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { resolvePaymentSlipParties } from "../../pdf-templates/pdf-template-components/qr-payment-slip/payment-slip-parties";
import { XmlEndpointsEnum, useExportXml } from "../useExportXml/useExportXml";
import { useCentralStore } from "../../store/Central";
import { showNotification } from "../../store/Central/selectors";
import { useStorage } from "../useStorage";
import { getBankAccount } from "./get-bank-account/get-bank-account";
import { usePatientStore } from "../../store/Patient";
import { createCustomFooterTemplate } from "../../pdf-templates/pdf-template-components/pdf-custom-footer-template.component";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import {
	getCurrencyFromRegion,
	hidePaymentSlip,
} from "../../lib/utils/region-utils";

export interface OperationResult {
	success: boolean;
	error: string | null;
	blob?: Blob | null;
}

export interface CompleteJobDocumentInformation {
	client: ClientEntityType;
	job: JobEntityType;
	jobDocument: JobDocumentEntityType;
	jobItems: JobItemEntityType[];
}

export interface DocumentPropsForReturnType {
	organization: OrganizationEntityType;
	job: JobEntityType;
	jobDocument: JobDocumentEntityType;
	jobItems: JobItemEntityType[];
	client: ClientEntityType;
	jobTotals: CalculateTotalsResult;
	guarantor: GuarantorEntityType | undefined;
	practitioner: PractitionerEntityType | undefined;
	patient: PatientEntityType | undefined;
}

/**
 * Scans every document/region comment in the PDF config for #WAEHRUNGSRECHNER:BASE:GOAL
 * tokens, fetches each rate from frankfurter.dev (proxied + cached by express-backend),
 * and returns a map of full-token -> converted amount string (e.g. "EUR 1234.56").
 * Failed/unavailable rates map to "" so the token renders empty rather than breaking the
 * export. Returns {} (no network call) when no converter token is present.
 */
export const resolveCurrencyConverterValues = async (
	totalValue: number,
	config: PdfTemplateConfiguration | undefined
): Promise<Record<string, string>> => {
	if (!config) {
		return {};
	}

	const sections = Object.values(config) as Array<{
		commentText?: RTF;
		commentTextByRegion?: Partial<Record<number, RTF>>;
	}>;

	const text = sections
		.flatMap((section) => [
			section.commentText?.text ?? "",
			...Object.values(section.commentTextByRegion ?? {}).map(
				(rtf) => rtf?.text ?? ""
			),
		])
		.join("\n");

	const pairs = extractCurrencyConverterPairs(text);
	if (pairs.length === 0) {
		return {};
	}

	// Deduplicate by full token so each unique currency pair is fetched only once.
	const uniquePairs = Array.from(
		new Map(pairs.map((pair) => [pair.token, pair])).values()
	);

	const values: Record<string, string> = {};
	await Promise.all(
		uniquePairs.map(async ({ token, base, goal }) => {
			try {
				const { rate } = await trpc.currency.getRate.query({
					base,
					goal,
				});
				values[token] =
					rate != null
						? `${goal} ${(totalValue * rate).toFixed(2)}`
						: "";
			} catch {
				values[token] = "";
			}
		})
	);

	return values;
};

export const documentPropsFor = async (
	organization: OrganizationEntityType,
	props: CompleteJobDocumentInformation,
	akontoAmount?: number
): Promise<DocumentPropsForReturnType | null> => {
	const { client, job, jobDocument, jobItems } = props;
	return {
		organization,
		job,
		jobDocument,
		jobItems,
		client,
		jobTotals: calculateJobTotalsLegacy({
			job,
			jobDocument,
			jobItems,
			organization,
			client,
			akontoAmount,
		}),
		guarantor: job.guarantor_id
			? useCentralStore.getState().guarantorLookup[job.guarantor_id]
			: undefined,
		patient: job.patient_id
			? ((await usePatientStore.getState().getPatient(job.patient_id)) ??
				undefined)
			: undefined,
		practitioner: job.practitioner_id
			? useCentralStore.getState().practitionersLookup[
					job.practitioner_id
				]
			: undefined,
	};
};

export const useExportActions = () => {
	const { pdfTemplateConfiguration } = usePdfTemplateConfigurationContext();
	const { bankAccountsLookup, clientsLookup, activatedRegions } =
		useCentralStore((state) => ({
			bankAccountsLookup: state.bankAccountsLookup,
			clientsLookup: state.clientsLookup,
			activatedRegions: state.activatedRegions,
		}));
	const { exportPdf } = useExportPdf();
	const { exportXml } = useExportXml();
	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));

	const getCustomFooterTemplate = (templateType: PdfTemplateType) => {
		if (
			!pdfTemplateConfiguration?.general?.customFooterEnabled &&
			!pdfTemplateConfiguration?.general?.backgroundImageEnabled
		) {
			return undefined;
		}

		return createCustomFooterTemplate(
			pdfTemplateConfiguration,
			templateType
		);
	};

	const getCustomFooterHeight = () => {
		return pdfTemplateConfiguration?.general?.customFooterEnabled
			? pdfTemplateConfiguration.general.customFooterHeight || 15
			: 0;
	};

	// Resolves all #WAEHRUNGSRECHNER:BASE:GOAL tokens used in the PDF config, converting
	// the given document total. Base/goal currencies are encoded in the token itself.
	const currencyConverterValuesFor = (
		totalValue: number
	): Promise<Record<string, string>> =>
		resolveCurrencyConverterValues(totalValue, pdfTemplateConfiguration);

	const { fileUrl } = useStorage();

	const paymentSlipDataFor = (
		props: DocumentPropsForReturnType,
		invoiceNumber: string,
		amountOverride?: string
	): PaymentSlipDataInput | null => {
		if (!organization) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return null;
		}
		const { client, guarantor, patient, job, jobDocument, jobItems } =
			props;

		const { debtor, bankAccountOwner } = resolvePaymentSlipParties({
			job,
			client,
			guarantor,
			patient: patient as PatientEntityType | null,
		});

		const { bankAccount, error: bankAccountError } = getBankAccount({
			bankAccountsLookup,
			lab: organization,
			job,
			recipient: bankAccountOwner,
		});
		if (!bankAccount) {
			showNotification({
				message: bankAccountError,
				type: "error",
			});
			return null;
		}

		const amount =
			amountOverride ??
			calculateJobTotalsLegacy({
				job,
				jobDocument,
				jobItems,
				organization,
				client,
			}).total.text;

		const currency = getCurrencyFromRegion(
			client,
			organization,
			activatedRegions
		);

		const paymentSlipData: PaymentSlipDataInput = {
			bankAccount,
			recipient: debtor,
			amount,
			invoiceNumber,
			currency,
		};
		return paymentSlipData;
	};

	const getElementForKostenvoranschlag = (
		documentProps: Omit<
			React.ComponentProps<typeof KostenvoranschlagTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<KostenvoranschlagTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForLieferschein = (
		documentProps: Omit<
			React.ComponentProps<typeof LieferscheinTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<LieferscheinTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForAuftragsblatt = (
		documentProps: Omit<
			React.ComponentProps<typeof AuftragsblattTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<AuftragsblattTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForRechnung = (
		documentProps: Omit<
			React.ComponentProps<typeof RechnungTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<RechnungTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForGutschrift = (
		documentProps: Omit<
			React.ComponentProps<typeof GutschriftTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<GutschriftTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForBarzahlungsquittung = (
		documentProps: Omit<
			React.ComponentProps<typeof BarzahlungsquittungTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<BarzahlungsquittungTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForKartenzahlungsquittung = (
		documentProps: Omit<
			React.ComponentProps<typeof KartenzahlungsquittungTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<KartenzahlungsquittungTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForAkontorechnung = (
		documentProps: Omit<
			React.ComponentProps<typeof AkontorechnungTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<AkontorechnungTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForSammelrechnung = (
		documentProps: Omit<
			React.ComponentProps<typeof SammelrechnungTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<SammelrechnungTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForJobOrderLabel = (
		documentProps: Omit<
			React.ComponentProps<typeof JobOrderLabelTemplate>,
			"pdfTemplateConfiguration"
		>
	) => {
		return (
			<JobOrderLabelTemplate
				{...documentProps}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
			/>
		);
	};

	const getElementForDebitorenExport = (
		documentProps: React.ComponentProps<typeof DebitorenExportTemplate>
	) => {
		return <DebitorenExportTemplate {...documentProps} />;
	};

	const exportRechnung = async (
		documentProps: DocumentPropsForReturnType,
		paymentSlipData: PaymentSlipDataInput,
		patient: PatientEntityType | undefined,
		filePath?: string,
		noPaymentSlip?: boolean
	): Promise<OperationResult> => {
		const shouldHidePaymentSlip =
			noPaymentSlip ||
			hidePaymentSlip(
				documentProps.client,
				documentProps.organization,
				activatedRegions
			);

		const currencyConverterValues = await currencyConverterValuesFor(
			documentProps.jobTotals.total.value
		);

		const documentComponent = getElementForRechnung({
			...documentProps,
			currencyConverterValues,
			paymentSlipData,
			patient,
			noPaymentSlip: shouldHidePaymentSlip,
		});

		return await exportPdf({
			documentComponent,
			filePath,
			pageTemplate: getCustomFooterTemplate(PdfTemplateType.RECHNUNG),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	const exportBarzahlungsquittung = async (
		documentProps: DocumentPropsForReturnType,
		paymentSlipData: PaymentSlipDataInput,
		filePath?: string
	): Promise<OperationResult> => {
		const currencyConverterValues = await currencyConverterValuesFor(
			documentProps.jobTotals.total.value
		);

		const documentComponent = getElementForBarzahlungsquittung({
			...documentProps,
			currencyConverterValues,
			paymentSlipData,
			patient: documentProps.patient as PatientEntityType,
		});
		return await exportPdf({
			documentComponent,
			filePath,
			pageTemplate: getCustomFooterTemplate(
				PdfTemplateType.BARZAHLUNGSQUITTUNG
			),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	const exportKartenzahlungsquittung = async (
		documentProps: DocumentPropsForReturnType,
		paymentSlipData: PaymentSlipDataInput,
		filePath?: string
	): Promise<OperationResult> => {
		const currencyConverterValues = await currencyConverterValuesFor(
			documentProps.jobTotals.total.value
		);

		const documentComponent = getElementForKartenzahlungsquittung({
			...documentProps,
			currencyConverterValues,
			paymentSlipData,
			patient: documentProps.patient as PatientEntityType,
		});
		return await exportPdf({
			documentComponent,
			filePath,
			pageTemplate: getCustomFooterTemplate(
				PdfTemplateType.KARTENZAHLUNGSQUITTUNG
			),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	const exportAkontorechnung = async (
		documentProps: DocumentPropsForReturnType,
		paymentSlipData: PaymentSlipDataInput,
		date: string,
		filePath?: string,
		noPaymentSlip?: boolean
	): Promise<OperationResult> => {
		const shouldHidePaymentSlip =
			noPaymentSlip ||
			hidePaymentSlip(
				documentProps.client,
				documentProps.organization,
				activatedRegions
			);

		const currencyConverterValues = await currencyConverterValuesFor(
			documentProps.jobTotals.total.value
		);

		const documentComponent = getElementForAkontorechnung({
			...documentProps,
			currencyConverterValues,
			paymentSlipData,
			date,
			noPaymentSlip: shouldHidePaymentSlip,
		});
		return await exportPdf({
			documentComponent,
			filePath,
			pageTemplate: getCustomFooterTemplate(
				PdfTemplateType.AKONTORECHNUNG
			),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	interface ExportSammelrechnungProps {
		recipient: ClientEntityType | GuarantorEntityType;
		extendedJobDocuments: ExtendedJobDocument[];
		invoiceNumber: string;
		filePath?: string;
		invoiceDate: Date;
		noPaymentSlip?: boolean;
		akontoAmount: number;
	}

	const exportSammelrechnung = async ({
		recipient,
		extendedJobDocuments,
		invoiceNumber,
		filePath,
		invoiceDate,
		noPaymentSlip,
		akontoAmount,
	}: ExportSammelrechnungProps): Promise<OperationResult> => {
		if (!organization) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return {
				success: false,
				error: "Organisation nicht gefunden",
			};
		}

		const { bankAccount, error: bankAccountError } = getBankAccount({
			bankAccountsLookup,
			lab: organization,
			job: null,
			recipient,
		});
		if (!bankAccount) {
			showNotification({
				message: bankAccountError,
				type: "error",
			});
			return {
				success: false,
				error: bankAccountError,
			};
		}
		const { mwstPercentage, error } =
			getMwStPercentageForSammelrechnung(extendedJobDocuments);
		if (error) {
			showNotification({
				message: "Alle Aufträge müssen den gleichen MwSt.-Satz haben",
				type: "error",
			});
			return {
				success: false,
				error: "Alle Aufträge müssen den gleichen MwSt.-Satz haben",
			};
		}

		const sammelrechnungCalculationResult =
			calculateSammelrechnungRowsAndTotals(
				extendedJobDocuments,
				organization,
				recipient,
				clientsLookup,
				mwstPercentage,
				akontoAmount
			);

		// Get the client from the first job document for display settings
		const firstJobClientId = extendedJobDocuments[0]?.job_client_id;
		const client = clientsLookup[firstJobClientId as string];

		const currency = getCurrencyFromRegion(
			client,
			organization,
			activatedRegions
		);

		const paymentSlipData: PaymentSlipDataInput = {
			bankAccount: bankAccount,
			recipient,
			amount: sammelrechnungCalculationResult.total.text,
			invoiceNumber,
			currency,
		};

		if (!client) {
			showNotification({
				message: "Client nicht gefunden für Sammelrechnung",
				type: "error",
			});
		}

		const shouldHidePaymentSlip =
			noPaymentSlip ||
			hidePaymentSlip(client, organization, activatedRegions);

		const currencyConverterValues = await resolveCurrencyConverterValues(
			sammelrechnungCalculationResult.total.value,
			pdfTemplateConfiguration
		);

		const documentProps = {
			recipient,
			client,
			sammelrechnungCalculationResult,
			organization,
			paymentSlipData,
			currencyConverterValues,
			logoUrl: pdfTemplateConfiguration?.general.logoImgurl,
			currentDate: invoiceDate.toISOString(),
			noPaymentSlip: shouldHidePaymentSlip,
		};
		const element = getElementForSammelrechnung(documentProps);
		return await exportPdf({
			documentComponent: element,
			filePath,
			pageTemplate: getCustomFooterTemplate(
				PdfTemplateType.SAMMELRECHNUNG
			),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	const exportKostenvoranschlag = async (
		documentProps: DocumentPropsForReturnType,
		filePath: string,
		patient?: PatientEntityType
	): Promise<OperationResult> => {
		const currencyConverterValues = await currencyConverterValuesFor(
			documentProps.jobTotals.total.value
		);

		const element = getElementForKostenvoranschlag({
			...documentProps,
			currencyConverterValues,
			patient,
		});
		return await exportPdf({
			documentComponent: element,
			filePath,
			pageTemplate: getCustomFooterTemplate(
				PdfTemplateType.KOSTENVORANSCHLAG
			),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	const exportLieferschein = async (
		documentProps: DocumentPropsForReturnType,
		filePath: string,
		patient?: PatientEntityType
	): Promise<OperationResult> => {
		const currencyConverterValues = await currencyConverterValuesFor(
			documentProps.jobTotals.total.value
		);

		const element = getElementForLieferschein({
			...documentProps,
			currencyConverterValues,
			patient,
		});
		return await exportPdf({
			documentComponent: element,
			filePath,
			pageTemplate: getCustomFooterTemplate(PdfTemplateType.LIEFERSCHEIN),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	const exportAuftragsblatt = async (
		props: {
			job: JobEntityType;
			client: ClientEntityType;
			organization: OrganizationEntityType;
			patient?: PatientEntityType;
			practitioner?: PractitionerEntityType;
			guarantor?: GuarantorEntityType;
			qrCodeUrl: string;
			formSections?: any[];
			formData?: Record<string, any>;
			formReferences?: Record<string, string>;
			formTitle?: string;
			odontogramData?: any[];
			odontogramFields?: any[];
			deliveryNoteItems?: JobItemEntityType[];
			deliveryNoteDocument?: JobDocumentEntityType;
			fileThumbnails?: Record<string, string>;
			excludedFieldIds?: string[];
			jobFiles?: Array<{ name: string; url?: string; type: string }>;
		},
		filePath: string
	): Promise<OperationResult> => {
		const element = getElementForAuftragsblatt(props);
		return await exportPdf({
			documentComponent: element,
			filePath,
			paperSize: ["148mm", "210mm"],
			scale: 0.75,
		});
	};

	const exportGutschrift = async (
		documentProps: DocumentPropsForReturnType,
		filePath: string,
		patient?: PatientEntityType
	): Promise<OperationResult> => {
		const currencyConverterValues = await currencyConverterValuesFor(
			documentProps.jobTotals.total.value
		);

		const element = getElementForGutschrift({
			...documentProps,
			currencyConverterValues,
			patient,
		});
		return await exportPdf({
			documentComponent: element,
			filePath,
			pageTemplate: getCustomFooterTemplate(PdfTemplateType.GUTSCHRIFT),
			customFooterHeight: getCustomFooterHeight(),
		});
	};

	const exportXmlLieferschein = async (
		documentProps: CompleteJobDocumentInformation,
		patient: PatientEntityType,
		fileName: string
	) => {
		return exportXml({
			documentProps,
			patient,
			endpoint: XmlEndpointsEnum.DELIVERY_NOTE,
			fileName: fileName,
		});
	};
	const exportXmlKostenvoranschlag = async (
		props: CompleteJobDocumentInformation,
		patient: PatientEntityType,
		fileName: string
	) => {
		if (!organization) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return {
				success: false,
				error: "Organisation nicht gefunden",
			};
		}
		const documentProps = await documentPropsFor(organization, props);
		if (!documentProps) {
			return {
				success: false,
				error: "Dokumentinformationen konnten nicht erstellt werden",
			};
		}
		return exportXml({
			documentProps,
			patient,
			endpoint: XmlEndpointsEnum.QUOTATION,
			fileName: fileName,
		});
	};

	const exportJobOrderLabel = async (
		documentProps: {
			job: JobEntityType;
			client: ClientEntityType;
			organization: OrganizationEntityType;
			qrCodeUrl?: string;
			patient?: PatientEntityType;
		},
		filePath: string
	): Promise<OperationResult> => {
		const qrCodeUrl =
			documentProps.qrCodeUrl ||
			"https://app.dentlab.ch" + `/job/${documentProps.job?.id}`;
		const element = getElementForJobOrderLabel({
			...documentProps,
			qrCodeUrl,
		});
		return await exportPdf({
			documentComponent: element,
			filePath,
			paperSize: ["100mm", "32mm"],
			pageTemplate: undefined,
			customFooterHeight: undefined,
		});
	};

	const exportDebitorenExport = async (
		exportData: ExportRowData[],
		summary: ExportSummary,
		headerMetadata?: ExportHeaderMetadata
	): Promise<OperationResult> => {
		const element = getElementForDebitorenExport({
			exportData,
			summary,
			headerMetadata,
		});
		const pageTemplate = headerMetadata
			? createDebitorenHeaderTemplate(headerMetadata)
			: undefined;

		return await exportPdf({
			documentComponent: element,
			paperSize: ["210mm", "297mm"],
			scale: 0.7,
			pageTemplate,
			customHeaderHeight: headerMetadata ? 20 : undefined,
		});
	};

	const exportPaymentReminder = async (
		document: PaymentReminderDocument,
		filePath?: string
	): Promise<OperationResult> => {
		const client = document.client
			? clientsLookup[document.client.id] ?? null
			: null;
		const slipsHidden =
			!!organization &&
			hidePaymentSlip(client, organization, activatedRegions);
		const bankAccountId =
			document.invoice.bank_account_id ??
			organization?.default_bank_account_id;
		const bankAccount =
			!slipsHidden && bankAccountId
				? bankAccountsLookup[bankAccountId] ?? null
				: null;
		if (!slipsHidden && hasReminderSlips(document)) {
			const slipError = bankAccount
				? getReminderSlipError(document, bankAccount)
				: "Kein Bankkonto für den Zahlschein hinterlegt.";
			if (slipError) {
				return { success: false, error: `Zahlschein: ${slipError}` };
			}
		}
		const templateType = document.reminder.template_type;
		const currencyConverterValues = await currencyConverterValuesFor(
			document.invoice.open_amount
		);

		return await exportPdf({
			documentComponent: (
				<PaymentReminderTemplate
					document={document}
					organization={organization}
					pdfTemplateConfiguration={pdfTemplateConfiguration}
					bankAccount={bankAccount}
					currencyConverterValues={currencyConverterValues}
				/>
			),
			filePath,
			pageTemplate: getCustomFooterTemplate(templateType),
			customFooterHeight: getCustomFooterHeight(),
			forcePageBreak: `.${PAYMENT_REMINDER_PAGE_BREAK}`,
		});
	};

	return {
		paymentSlipDataFor,
		exportRechnung,
		exportBarzahlungsquittung,
		exportKartenzahlungsquittung,
		exportAkontorechnung,
		exportSammelrechnung,
		exportKostenvoranschlag,
		exportLieferschein,
		exportAuftragsblatt,
		exportGutschrift,
		exportXmlLieferschein,
		exportXmlKostenvoranschlag,
		exportJobOrderLabel,
		exportDebitorenExport,
		exportPaymentReminder,
	};
};
